import { motion } from 'framer-motion'
import { Check, Shield, Sparkles, Users, Zap } from 'lucide-react'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { authApi } from '@/api/client'

type Tier = {
  name: string
  price: string
  pricePer: string
  description: string
  aiBadge: string
  features: string[]
  icon: React.ReactNode
  cta: string
  highlighted?: boolean
}

const tiers: Tier[] = [
  {
    name: 'Basic',
    price: '$5',
    pricePer: '/mo',
    description: 'For privacy-conscious developers who want a no-frills auditing home.',
    aiBadge: 'Self-hosted AI',
    features: [
      'Unlimited Lighthouse audits',
      '15 AI suggestion calls / month',
      'Self-hosted model — your code never leaves our private network',
      'Local + Git source attachment',
      'Full audit history (no expiry)',
      'Compare audits over time',
    ],
    icon: <Shield size={20} className="text-accent" />,
    cta: 'Subscribe to Basic',
  },
  {
    name: 'Pro',
    price: '$9',
    pricePer: '/mo',
    description: 'For solo developers who want frontier-quality AI suggestions.',
    aiBadge: 'Claude Haiku 4.5',
    features: [
      'Unlimited Lighthouse audits',
      '20 AI suggestion calls / month',
      'Powered by Claude Haiku 4.5 — sharper, faster fixes',
      'Local + Git source attachment',
      'Full audit history (no expiry)',
      'Compare audits over time',
      'Export HTML reports',
    ],
    icon: <Sparkles size={20} className="text-accent" />,
    cta: 'Subscribe to Pro',
    highlighted: true,
  },
  {
    name: 'Team',
    price: '$29',
    pricePer: '/mo + $5/seat',
    description: 'For engineering teams that want shared workspaces and more AI headroom.',
    aiBadge: 'Claude Haiku 4.5',
    features: [
      'Everything in Pro',
      '30 AI suggestion calls / month',
      'Shared workspace with your team',
      'Invite up to 10 members',
      'Per-workspace audit history',
      'Priority support',
    ],
    icon: <Users size={20} className="text-accent" />,
    cta: 'Subscribe to Team',
  },
]

export default function SubscribePage() {
  const { data: user } = useCurrentUser()

  return (
    <PageTransition>
      <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex justify-center mb-4">
            <Zap size={36} className="text-accent" fill="currentColor" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">
            Pick a plan that fits.
          </h1>
          <p className="text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
            All paid plans start with a <b>14-day free trial</b> — unlimited audits + 15 self-hosted AI calls. No card required upfront.
          </p>
          {!user && (
            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              <a href={authApi.googleSignInUrl()} className="text-accent hover:underline">Sign in</a>{' '}
              to start your trial.
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
            >
              <GlassCard
                className={`h-full flex flex-col ${tier.highlighted ? 'border-accent/40' : ''}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  {tier.icon}
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">{tier.name}</h2>
                  {tier.highlighted && (
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent/15 text-accent">
                      Most popular
                    </span>
                  )}
                </div>

                <div className="mb-2">
                  <span className="text-3xl font-bold text-[var(--text-primary)]">{tier.price}</span>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{tier.pricePer}</span>
                </div>

                <div className="mb-3">
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10"
                        style={{ color: 'var(--text-secondary)' }}>
                    {tier.aiBadge}
                  </span>
                </div>

                <p className="text-xs mb-5" style={{ color: 'var(--text-secondary)' }}>
                  {tier.description}
                </p>

                <ul className="space-y-2 mb-6 flex-1">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-xs">
                      <Check size={14} className="text-accent shrink-0 mt-0.5" />
                      <span style={{ color: 'var(--text-secondary)' }}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  disabled
                  title="Billing goes live with Phase B"
                  className="w-full py-2.5 rounded-lg text-sm font-semibold border transition-all duration-200
                             border-white/10 text-[var(--text-muted)] bg-white/[0.02] cursor-not-allowed"
                >
                  {tier.cta} — coming soon
                </button>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-[11px] mt-8" style={{ color: 'var(--text-muted)' }}>
          Per-plan AI providers (Claude Haiku 4.5 on Pro/Team) go live together with checkout —
          until then, all AI suggestions run on our self-hosted model.
        </p>
        <p className="text-center text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
          Annual plans (2 months free) ship alongside checkout. Questions? <a className="text-accent hover:underline" href="mailto:mihai.parlog@mpm.technology">Email us</a>.
        </p>
      </div>
    </PageTransition>
  )
}
